type CoverageType = 'Essential' | 'Cosmetic' | 'Plus' | 'Premier';
type FuelType = 'gas' | 'hybrid' | 'electric';

// Base features that apply to all fuel types
const BASE_COVERAGE_FEATURES: Record<CoverageType, string[]> = {
  Essential: ['Powertrain components, such as the engine and transmission', 'Roadside assistance'],
  Cosmetic: [
    'Paint-less dent repairs, tire and wheel repairs, plus tire rotations and oil changes for the first year of ownership',
    'Roadside assistance'
  ],
  Plus: [
    'Front-to-back coverage for mechanical and electrical components',
    'Air conditioning, heating, power steering and more',
    'Roadside assistance'
  ],
  Premier: [
    'Front-to-back coverage for mechanical and electrical components',
    'Air conditioning, heating, power steering and more',
    'Paint-less dent repairs, tire and wheel repairs, plus tire rotations and oil changes for the first year of ownership',
    'Roadside assistance'
  ]
};

// Additional features specific to EV/Hybrid vehicles (same across all coverage types)
export const FUEL_SPECIFIC_ADDITIONS: Record<FuelType, string[]> = {
  gas: [],
  hybrid: ['Battery pack, electric motor, and inverter'],
  electric: ['EV Powertrain components including battery pack, motor and inverter']
};

/**
 * Determines fuel type from vehicle fuel description
 */
export const getFuelType = (fuelDescription?: string): FuelType => {
  if (!fuelDescription) return 'gas'; // Default to gas for safety

  const normalizedFuel = fuelDescription.toLowerCase();

  if (normalizedFuel.includes('electric')) return 'electric';
  if (normalizedFuel.includes('hybrid')) return 'hybrid';

  return 'gas';
};

/**
 * Gets coverage features for a specific coverage type and fuel type
 */
export const getCoverageFeatures = (coverageType: CoverageType, fuelType?: FuelType): string[] => {
  const resolvedFuelType = fuelType ?? 'gas';
  const baseFeatures = BASE_COVERAGE_FEATURES[coverageType] ?? BASE_COVERAGE_FEATURES.Essential;

  // Don't include fuel_specific_additions for EV/Hybrid when coverageType is Cosmetic
  const shouldExcludeFuelAdditions =
    coverageType === 'Cosmetic' &&
    (resolvedFuelType === 'electric' || resolvedFuelType === 'hybrid');

  const fuelSpecificAdditions = shouldExcludeFuelAdditions
    ? []
    : FUEL_SPECIFIC_ADDITIONS[resolvedFuelType] ?? [];

  return [...fuelSpecificAdditions, ...baseFeatures];
};

export default getCoverageFeatures;
